import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { getLeads, saveLeads, getSettings } from "./demoConfig";
import { monthlyResults } from "../data/mock";
import DemoScreens from "./DemoScreens";
import CloserSidebar from "./CloserSidebar";
import WindowPortal from "./WindowPortal";
import { Monitor, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";

const TOTAL_SCREENS = 12;
const API = process.env.REACT_APP_BACKEND_URL || "";

const SCREEN_TITLES = [
  "Welcome",
  "About You",
  "The Numbers Game",
  "Real Results",
  "The 3 Paths",
  "Our System",
  "Your First Week",
  "Your Dashboard",
  "Everything You Get",
  "The Investment",
  "Your Plan",
  "Let's Get Started",
];

const CLOSER_TITLES = [
  "Welcome & Intro",
  "Qualify / Diagnose",
  "Gap Reveal",
  "Proof Wall",
  "3 Ways Education",
  "3-Pronged Attack",
  "Day 1 Process",
  "Dashboard Demo",
  "The Stack",
  "ROI Calculator",
  "Offer / Pricing",
  "Close & Payment",
];

export default function LiveDemo() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { auth } = useOutletContext();
  const [lead, setLead] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(1);
  const [showShareModal, setShowShareModal] = useState(true);

  // Screen 2 (Qualify)
  const [weeklyApps, setWeeklyApps] = useState("");
  const [confirmedRole, setConfirmedRole] = useState("");
  const [confirmedMarket, setConfirmedMarket] = useState("");
  const [linkedInConnections, setLinkedInConnections] = useState("");
  const [linkedInReferrals, setLinkedInReferrals] = useState("");
  const [monthsSearching, setMonthsSearching] = useState("");

  // Screen 4 (Proof Wall)
  const [gmailMode, setGmailMode] = useState(false);
  const [selectedCaseStudies, setSelectedCaseStudies] = useState([]);
  const [proofTab, setProofTab] = useState("pain");
  const [pdfMonth, setPdfMonth] = useState(monthlyResults.length - 1);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfTotalPages, setPdfTotalPages] = useState(null);

  // Screen 5 (3 Ways)
  const [revealedWays, setRevealedWays] = useState([false, false, false]);

  // Screen 6 (Our System)
  const [revealedPanels, setRevealedPanels] = useState([false, false, false]);

  // Screen 9 (The Stack)
  const [revealedStackItems, setRevealedStackItems] = useState(0);

  // Screen 10 (ROI)
  const [salaryInput, setSalaryInput] = useState("");
  const [monthsInput, setMonthsInput] = useState("");

  // Screen 11 (Offer)
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [offerPhase, setOfferPhase] = useState(1); // 1 = Guaranteed only, 2 = all plans

  // Screen 12 (Close)
  const [paymentMethod, setPaymentMethod] = useState(null);

  // Popout mode
  const [popoutMode, setPopoutMode] = useState(false);
  const [popoutReady, setPopoutReady] = useState(false);

  // Timer
  const [elapsed, setElapsed] = useState(0);
  const startTime = useRef(Date.now());

  // Demo session anchor — when the closer actually fired up this LiveDemo.
  // Used by Screen 7 ("Your First Week" calendar dates) when the lead has
  // no scheduledAt (walk-in / unscheduled demos).
  const demoStartedAtRef = useRef(new Date().toISOString());

  useEffect(() => {
    const leads = getLeads();
    const found = leads.find((l) => l.id === leadId);
    if (!found) { navigate("/demo/crm"); return; }
    setLead(found);
    setWeeklyApps(found.weeklyApps || "");
    setConfirmedRole(found.targetRole || "");
    setConfirmedMarket(found.targetMarket || "");
    setSalaryInput(found.targetSalary || "");
    setMonthsInput(found.monthsSearching || "");
    setMonthsSearching(found.monthsSearching || "");
  }, [leadId, navigate]);

  // Pre-fill monthsInput from monthsSearching when entering ROI screen
  useEffect(() => {
    if (currentScreen === 10 && monthsSearching && !monthsInput) {
      setMonthsInput(monthsSearching);
    }
  }, [currentScreen, monthsSearching, monthsInput]);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startTime.current) / 1000)), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Timer: runs once on mount, uses ref for start time

  useEffect(() => {
    const handler = () => setPopoutReady(true);
    window.addEventListener("popout-ready", handler);
    return () => window.removeEventListener("popout-ready", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Global event listener: runs once on mount

  const updateLeadField = async (updates) => {
    const leads = getLeads();
    const updated = leads.map((l) => (l.id === leadId ? { ...l, ...updates } : l));
    saveLeads(updated);
    setLead((prev) => ({ ...prev, ...updates }));
    // Sync to backend API
    try {
      await fetch(`${API}/api/demo/leads/${leadId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
    } catch (e) {
      console.error("Failed to sync lead update to backend:", e);
    }
  };

  const getRecommendedPlan = () => {
    const months = parseFloat(monthsInput || monthsSearching);
    if (months >= 4) return "guaranteed";
    if (!months || months < 1) return "1-month-full-stack";
    return "2-month-full-stack";
  };

  const endDemo = () => {
    updateLeadField({
      status: "demo_done",
      demoDoneAt: new Date().toISOString(),
      weeklyApps: parseFloat(weeklyApps) || null,
      targetSalary: parseFloat(salaryInput) || null,
      monthsSearching: parseFloat(monthsInput || monthsSearching) || null,
      recommendedPlan: getRecommendedPlan(),
      selectedPlan: selectedPlan || getRecommendedPlan(),
      painPoints: selectedCaseStudies,
    });
    setPopoutMode(false);
    navigate("/demo/crm");
  };

  const revealPanel = (idx) => {
    setRevealedPanels((p) => { const n = [...p]; n[idx] = true; return n; });
  };

  const revealWay = (idx) => {
    setRevealedWays((p) => { const n = [...p]; n[idx] = true; return n; });
  };

  const revealNextStackItem = () => {
    setRevealedStackItems((n) => Math.min(n + 1, 7));
  };

  const toggleCaseStudy = (id) => {
    setSelectedCaseStudies((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const formatTimer = () => {
    const m = Math.floor(elapsed / 60);
    const s = elapsed % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handlePopoutClose = useCallback(() => {
    setPopoutMode(false);
    setPopoutReady(false);
  }, []);

  if (!lead) return null;

  const settings = getSettings();

  const sharedState = {
    lead, currentScreen, weeklyApps, confirmedRole, confirmedMarket,
    linkedInConnections, linkedInReferrals, monthsSearching,
    gmailMode, selectedCaseStudies, proofTab, pdfMonth, pdfPage, pdfTotalPages,
    revealedWays, revealedPanels, revealedStackItems,
    salaryInput, monthsInput, selectedPlan, offerPhase,
    paymentMethod,
    recommendedPlan: getRecommendedPlan(), settings, auth,
    // Anchor for Screen 7 calendar dates when scheduledAt is missing (walk-in demos).
    demoStartedAt: demoStartedAtRef.current,
  };

  const sharedActions = {
    setCurrentScreen, setWeeklyApps, setConfirmedRole, setConfirmedMarket,
    setLinkedInConnections, setLinkedInReferrals, setMonthsSearching,
    setGmailMode, toggleCaseStudy, setProofTab, setPdfMonth, setPdfPage, setPdfTotalPages,
    revealWay, revealPanel, revealNextStackItem,
    setSalaryInput, setMonthsInput, setSelectedPlan, setOfferPhase,
    setPaymentMethod,
    updateLeadField, endDemo,
  };

  const sidebarProps = {
    state: sharedState,
    actions: sharedActions,
    elapsed: formatTimer(),
    screenTitles: SCREEN_TITLES,
    closerTitles: CLOSER_TITLES,
    popoutMode,
    onPopout: () => setPopoutMode(true),
  };

  return (
    <div className="live-demo-container" data-testid="live-demo-container">
      {showShareModal && (
        <div className="share-modal-overlay" data-testid="share-modal">
          <div className="share-modal-card">
            <div className="share-modal-icon-wrap">
              <Monitor className="share-modal-icon" />
            </div>
            <h2>Before you share your screen</h2>
            <p>Share <strong>only the left panel</strong> with the client. Use the <strong>Pop Out</strong> button in the sidebar to detach your controls into a separate window — invisible to the client.</p>
            <div className="share-modal-steps">
              <div className="share-step"><span className="share-step-num">1</span>Click <strong>"Pop Out"</strong> in the sidebar</div>
              <div className="share-step"><span className="share-step-num">2</span>Share this tab via Zoom/Meet</div>
              <div className="share-step"><span className="share-step-num">3</span>Your controls stay in the popup — client sees nothing</div>
            </div>
            <Button onClick={() => setShowShareModal(false)} className="share-modal-btn" data-testid="dismiss-share-modal">
              Got it, start demo
            </Button>
          </div>
        </div>
      )}

      <div className={`live-demo-split ${popoutMode ? "live-demo-fullwidth" : ""}`}>
        <div className="live-demo-left" data-testid="demo-left-panel">
          <div className="live-demo-left-header">
            <span className="live-screen-num">{currentScreen}/{TOTAL_SCREENS}</span>
            <span className="live-screen-label">{SCREEN_TITLES[currentScreen - 1]}</span>
            {popoutMode && (
              <span className="popout-indicator">
                <ExternalLink className="w-3.5 h-3.5" /> Controls in popup
              </span>
            )}
          </div>
          <div className="live-demo-left-content">
            <DemoScreens state={sharedState} actions={sharedActions} />
          </div>
          <div className="live-demo-left-footer">
            <Button variant="outline" size="sm" disabled={currentScreen <= 1} onClick={() => setCurrentScreen((s) => s - 1)} data-testid="prev-screen-btn" className="live-nav-btn">
              Previous
            </Button>
            <span className="live-footer-dots">
              {SCREEN_TITLES.map((_, i) => (
                <span key={`dot-${i}`} className={`live-dot ${i + 1 === currentScreen ? "active" : ""} ${i + 1 < currentScreen ? "done" : ""}`} />
              ))}
            </span>
            <Button size="sm" className="live-next-btn" disabled={currentScreen >= TOTAL_SCREENS} onClick={() => setCurrentScreen((s) => s + 1)} data-testid="next-screen-btn">
              Next
            </Button>
          </div>
        </div>

        {!popoutMode && (
          <div className="live-demo-right" data-testid="demo-right-panel">
            <CloserSidebar {...sidebarProps} />
          </div>
        )}

        {popoutMode && (
          <WindowPortal onClose={handlePopoutClose} title="Closer Controls — BulkJobApply">
            <div style={{ height: "100vh", overflow: "auto", background: "#fafafa" }}>
              <CloserSidebar {...sidebarProps} />
            </div>
          </WindowPortal>
        )}
      </div>
    </div>
  );
}
